/**
 * Four roles, per OMC_WEB_APP requirements:
 *   admin      — full access, including system administration and reference data
 *   supervisor — all operational functions, MAY approve plans
 *   planning   — all operational functions, may NOT approve
 *   viewer     — read only
 */
export const ROLES = ['admin', 'supervisor', 'planning', 'viewer'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  planning: 'Planning',
  viewer: 'Viewer',
}

export interface User {
  id: string
  email: string
  fullName: string
  role: Role
  /** Assigned duty shift, used for handover and event assignment. */
  shift?: string | null
  /** Force a password change on first sign-in for admin-issued accounts. */
  mustChangePassword: boolean
}

export interface Session {
  user: User
  /** Session expiry, epoch ms. Server clock once a backend exists. */
  expiresAt: number
}

/** Stable codes so the UI owns the wording, not the provider. */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'account_locked'
  | 'too_many_attempts'
  | 'network'
  | 'weak_password'
  | 'same_password'
  | 'session_expired'
  | 'unknown'

export class AuthError extends Error {
  code: AuthErrorCode
  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'AuthError'
    this.code = code
  }
}

export interface SignInInput {
  email: string
  password: string
}

export interface ChangePasswordInput {
  currentPassword: string
  newPassword: string
}

/**
 * Shared contract for every provider.
 * Swapping provider (mock -> Supabase -> in-house backend) must not touch the UI.
 */
export interface AuthProvider {
  readonly name: string
  /** Restore the session on reload. Null when signed out. */
  restore(): Promise<Session | null>
  signIn(input: SignInInput): Promise<Session>
  signOut(): Promise<void>
  changePassword(input: ChangePasswordInput): Promise<void>
  /** Refresh the session after user activity. */
  touch(): Promise<Session | null>
}
